/** Заявки операторов на допработы в ОО-дашборде (26.08, владелец): статус
 *  proposed, все даты. Клик по заявке — раскрыв: полный комментарий оператора и
 *  ОПЛАТА за допработу (rate_override — итоговая сумма, главнее часов в зарплате):
 *  авто = часы × ставка (дефолт 150₽/ч), правится NumPadPop. ✓/↩/✕ — вердикты.
 *  10.10 (владелец): долгое нажатие по блоку или «за период ›» — окно «Проверка
 *  допработ за период» (ExtraReview) от самой ранней заявки до сегодня.
 *  11.10 (владелец: «в дашборде пиздец… чтобы каждой дате — свои заявки, и сворачиваемы»):
 *  в списке только заявки и время по отчёту за дату дашборда; про другие дни — одна строка
 *  со счётом (ничего не теряется молча), тап по дате — дашборд на этот день (окно режет период
 *  до 62 дней — старые заявки иначе не найти). Тап по заголовку сворачивает блок, выбор помнится. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { NumPadPop } from "../../components/shared";
import { fmtDateApi, fromBusinessDate, parseDateApi, todayBusiness } from "../../utils/date";
import { ExtraReview } from "./ExtraReview";
import { useLongPress } from "./useLongPress";
import css from "./dashboard.module.css";

interface Prop { id: number; operator_name: string; date: string; time_from: string; time_to: string; description: string; hourly_rate: number; rate_override: number | null;
  /** 12.09: на этот день человеку уже назначена доп.работа — заявка похожа на дубль. */
  already_assigned?: boolean }

const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const toMin = (t: string) => { const [h, m] = t.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
/** Авто-оплата: часы × ставка; через полночь/битое время → 0. */
const autoPay = (p: Prop) => { const mins = toMin(p.time_to) - toMin(p.time_from); return mins > 0 ? Math.round((mins / 60) * (p.hourly_rate || 150)) : 0; };
const payOf = (p: Prop) => p.rate_override ?? autoPay(p);
const FOLD_KEY = "ub_oo_extra_props_folded";
/** дашборд на день d: через календарь шапки (OoAssignScreen слушает его onChange) */
const goDay = (d: string) => window.DateSelector?.setDate(fromBusinessDate(parseDateApi(d)));
const readFold = () => { try { return localStorage.getItem(FOLD_KEY) === "1"; } catch { return false; } };

export function ExtraProposals({ date }: { date: Date }) {
  const day = fmtDateApi(date);
  const [folded, setFolded] = useState(readFold);
  const toggleFold = () => setFolded((f) => { try { localStorage.setItem(FOLD_KEY, f ? "0" : "1"); } catch { /* приватный режим — просто не помним */ } return !f; });
  const [items, setItems] = useState<Prop[]>([]);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const [open, setOpen] = useState<number | null>(null);
  const [payFor, setPayFor] = useState<Prop | null>(null);
  const payAnchor = useRef<HTMLElement | null>(null);
  const [review, setReview] = useState(false);
  /* 10.10: запросы времени по назначенным (время по отчёту ждёт ОО) — раньше были заявками в этом же списке */
  const [facts, setFacts] = useState<(Prop & { fact_from?: string; fact_to?: string })[]>([]);
  const hold = useLongPress(() => setReview(true));
  useEffect(() => {
    let alive = true;
    api<{ results?: (Prop & { time_from?: string; time_to?: string })[] }>("/api/extra-work?status=proposed")
      .then((r) => { if (alive) setItems((r.results ?? []).map((p) => ({ ...p, time_from: (p.time_from ?? "").slice(0, 5), time_to: (p.time_to ?? "").slice(0, 5) }))); })
      .catch(() => {});
    api<{ results?: (Prop & { fact_from?: string; fact_to?: string })[] }>("/api/extra-work?status=fact_wait")
      .then((r) => { if (alive) setFacts((r.results ?? []).map((p) => ({ ...p, time_from: String(p.time_from ?? "").slice(0, 5), time_to: String(p.time_to ?? "").slice(0, 5) }))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tick]);
  if (!items.length && !facts.length && !review) return null;
  const dayItems = items.filter((p) => p.date.slice(0, 10) === day), dayFacts = facts.filter((p) => p.date.slice(0, 10) === day);
  const otherDays = new Map<string, number>();
  for (const p of [...items, ...facts]) { const d = p.date.slice(0, 10); if (d !== day) otherDays.set(d, (otherDays.get(d) ?? 0) + 1); }
  const otherN = [...otherDays.values()].reduce((a, b) => a + b, 0), hasDay = dayItems.length + dayFacts.length > 0;
  const act = (id: number, status: string) => void api(`/api/extra-work/${id}`, { method: "PUT", body: { status } }).then(reload).catch(() => {});
  const hours = (p: Prop) => Math.max(0, (toMin(p.time_to) - toMin(p.time_from)) / 60);
  return (
    <><section className={css.brief} {...hold}>
      <div className={css.briefRow} role="button" aria-expanded={hasDay ? !folded : undefined} title={hasDay ? (folded ? "Развернуть" : "Свернуть") : undefined} onClick={hasDay ? toggleFold : undefined}>
        <span className={css.briefLbl}>{hasDay ? (folded ? "▸ " : "▾ ") : ""}Заявки на доп · {dm(day)}</span>
        <span className={css.briefStat}><b>{dayItems.length}</b>{dayFacts.length ? <> · время <b>{dayFacts.length}</b></> : null}{otherN ? ` · ещё ${otherN} в др. дни` : ""}</span>
        <span className={css.propPeriod} role="button" title="Окно проверки допработ за период" onClick={(e) => { e.stopPropagation(); setReview(true); }}>за период ›</span>
      </div>
      {/* 12.09 (владелец): вердикты были только значками — подписываем, что они значат. */}
      {!folded && dayItems.length ? <div className={css.propLegend}>✓ принять · ↩ вернуть на исправление · ✕ отклонить</div> : null}
      {(folded ? [] : dayItems).map((p) => (
        <div key={p.id}>
          <div className={css.propRow} role="button" onClick={() => setOpen((o) => (o === p.id ? null : p.id))}>
            <span className={css.propWho}><b>{p.operator_name}</b> {dm(p.date)} {p.time_from}–{p.time_to}</span>
            <span className={css.propDesc} title={p.description}>{p.description}</span>
            {p.already_assigned
              ? <span className={css.propDup} title="На этот день работа уже назначена — возможно, оператор завёл её второй раз">дубль?</span>
              : null}
            <span className={css.propPayHint}>₽{payOf(p)}</span>
            <button type="button" className={`${css.propAct} ${css.propOk}`} title="Принять" onClick={(e) => { e.stopPropagation(); act(p.id, "confirmed"); }}>✓</button>
            <button type="button" className={`${css.propAct} ${css.propRw}`} title="На доработку" onClick={(e) => { e.stopPropagation(); act(p.id, "rework"); }}>↩</button>
            <button type="button" className={`${css.propAct} ${css.propNo}`} title="Отклонить" onClick={(e) => { e.stopPropagation(); act(p.id, "rejected"); }}>✕</button>
          </div>
          {open === p.id ? (
            <div className={css.propExpand}>
              {p.already_assigned ? (
                <div className={css.propComment}>
                  ⚠ На этот день этому человеку доп.работа уже назначена. Если он просто
                  переписал её заново — отклоните заявку (✕), назначенная останется.
                </div>
              ) : null}
              {p.description ? <div className={css.propComment}>💬 {p.description}</div> : <div className={css.propComment}>Без комментария</div>}
              <div className={css.propPayRow}>
                <span>Оплата за доп:</span>
                <button type="button" className={css.propPayBtn} title="Тап — изменить сумму"
                  onClick={(e) => { payAnchor.current = e.currentTarget; setPayFor(p); }}>₽{payOf(p)}</button>
                <span className={css.propPayAuto}>{p.rate_override != null ? `правлено · авто ₽${autoPay(p)}` : `авто: ${hours(p)} ч × ₽${p.hourly_rate || 150}/ч · время игр вычтется в расчёте`}</span>
              </div>
            </div>
          ) : null}
        </div>
      ))}
      {!folded && dayFacts.length ? <div className={css.propLegend}>Время по отчёту ждёт решения — до него платим по плану:</div> : null}
      {(folded ? [] : dayFacts).map((p) => (
        <div key={`f${p.id}`} className={css.propRow}>
          <span className={css.propWho}><b>{p.operator_name}</b> {dm(p.date)} {p.time_from}–{p.time_to} → {p.fact_from}–{p.fact_to}</span>
          <span className={css.propDesc} title={p.description}>{p.description}</span>
          <button type="button" className={`${css.propAct} ${css.propOk}`} title={`Платить по отчёту ${p.fact_from}–${p.fact_to}`} onClick={() => void api(`/api/extra-work/${p.id}`, { method: "PUT", body: { fact_ok: 1 } }).then(reload).catch(() => {})}>✓</button>
          <button type="button" className={`${css.propAct} ${css.propNo}`} title={`Оставить по плану ${p.time_from}–${p.time_to}`} onClick={() => void api(`/api/extra-work/${p.id}`, { method: "PUT", body: { fact_reset: true } }).then(reload).catch(() => {})}>✕</button>
        </div>
      ))}
      {otherN && (!hasDay || !folded) ? (
        /* 11.10 (владелец: «некрасиво смещается строка»): даты — ровным рядом плашек, перенос целыми плашками */
        <div className={css.propOther}><span className={css.propOtherLbl}>другие дни</span>{[...otherDays].sort(([a], [b]) => a.localeCompare(b)).map(([d, n]) => (
          <button key={d} type="button" className={css.propDay} title="Открыть этот день" onClick={(e) => { e.stopPropagation(); goDay(d); }}>{dm(d)}<b>{n}</b></button>))}</div>
      ) : null}
    </section>
      <NumPadPop
        open={!!payFor} anchor={payAnchor.current} label={`Оплата ₽ — ${payFor?.operator_name ?? ""}`}
        mode="money" value={payFor ? payOf(payFor) : 0} max={99999}
        onCommit={(v) => { if (payFor && v > 0 && v !== payOf(payFor)) void api(`/api/extra-work/${payFor.id}`, { method: "PUT", body: { rate_override: v } }).then(reload).catch(() => {}); }}
        onClose={() => setPayFor(null)}
      />
      {/* калькулятор и окно — вне секции: события из портала всплывают по дереву React, и долгое нажатие в них открывало окно или глотало клик */}
      {review ? (() => {
        const dates = [...items, ...facts].map((p) => p.date.slice(0, 10)).sort(), today = fmtDateApi(todayBusiness());
        const first = dates[0] ?? today, last = dates[dates.length - 1] ?? today;
        return <ExtraReview from={first} to={last > today ? last : today} onClose={() => { setReview(false); reload(); }} />;
      })() : null}</>
  );
}

